/** How G3-5 AE % was sourced from ruby_data (TEAE/TRAE columns or derived rates). */
export type G35AeSource = 'TEAE' | 'TRAE';

export interface ResolvedG35Ae {
  exp: number | null;
  ctrl: number | null;
  /** Set when value comes from TEAE or TRAE columns / rate fields; null for generic g3_5_ae or g3or4_ae. */
  source: G35AeSource | null;
}

function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value === 0) return null;
    return value;
  }
  const text = String(value).trim();
  if (!text || /^(n\/?a|null|none|nr|nd|-|—|–)$/i.test(text)) return null;
  // Accept "45%", "45.2 %", "1,234.5"
  const cleaned = text.replace(/,/g, '').replace(/%/g, '').trim();
  const match = cleaned.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const num = Number(match[0]);
  // 0 is stored as a placeholder in some ruby_data rows — treat as missing for SoC display.
  if (!Number.isFinite(num) || num === 0) return null;
  return num;
}

function sumRates(primary: number | null, g5: number | null): number | null {
  if (primary === null) return null;
  if (g5 === null) return primary;
  return Number((primary + g5).toFixed(1));
}

/** Sum whichever of G3 / G4 / G5 grade-level rates are present. */
function sumGradeComponents(
  g3: number | null,
  g4: number | null,
  g5: number | null,
): number | null {
  const parts = [g3, g4, g5].filter((n): n is number => n !== null);
  if (parts.length === 0) return null;
  return Number(parts.reduce((sum, n) => sum + n, 0).toFixed(1));
}

function normalizeFieldKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Read a field from top-level ruby_data columns and/or ae_dict, accepting
 * spreadsheet-style aliases like "G3 5 Ae Exp".
 */
function pickRawField(
  fields: Record<string, unknown>,
  aliases: string[],
): unknown {
  const aeDict =
    fields.ae_dict && typeof fields.ae_dict === 'object' && !Array.isArray(fields.ae_dict)
      ? (fields.ae_dict as Record<string, unknown>)
      : null;

  const pools: Array<Record<string, unknown>> = [fields];
  if (aeDict) pools.push(aeDict);

  const wanted = new Set(aliases.map(normalizeFieldKey));

  for (const pool of pools) {
    for (const alias of aliases) {
      if (Object.prototype.hasOwnProperty.call(pool, alias)) {
        const value = pool[alias];
        if (value !== null && value !== undefined && value !== '') return value;
      }
    }
    for (const [key, value] of Object.entries(pool)) {
      if (value === null || value === undefined || value === '') continue;
      if (wanted.has(normalizeFieldKey(key))) return value;
    }
  }
  return null;
}

function resolveG35Arm(
  g35Teae: unknown,
  g35Ae: unknown,
  g3or4Teae: unknown,
  g3or4Trae: unknown,
  g3or4Ae: unknown,
  g3Ae: unknown,
  g4Ae: unknown,
  g5: unknown,
): { value: number | null; source: G35AeSource | null } {
  const teaeDirect = parseNumber(g35Teae);
  if (teaeDirect !== null) return { value: teaeDirect, source: 'TEAE' };

  const g35Direct = parseNumber(g35Ae);
  if (g35Direct !== null) return { value: g35Direct, source: null };

  const g5v = parseNumber(g5);
  const teaeDerived = sumRates(parseNumber(g3or4Teae), g5v);
  if (teaeDerived !== null) return { value: teaeDerived, source: 'TEAE' };

  const traeDerived = sumRates(parseNumber(g3or4Trae), g5v);
  if (traeDerived !== null) return { value: traeDerived, source: 'TRAE' };

  const genericDerived = sumRates(parseNumber(g3or4Ae), g5v);
  if (genericDerived !== null) return { value: genericDerived, source: null };

  // Grade-level columns: g3_ae + g4_ae + g5ordeath (any subset present).
  const gradeSum = sumGradeComponents(parseNumber(g3Ae), parseNumber(g4Ae), g5v);
  if (gradeSum !== null && gradeSum !== 0) return { value: gradeSum, source: null };
  return { value: null, source: null };
}

export interface G35AeBackendFields {
  g3_5_teae_exp?: unknown;
  g3_5_teae_ctrl?: unknown;
  g3_5_ae_exp?: unknown;
  g3_5_ae_ctrl?: unknown;
  g3or4_teae_rate_exp?: unknown;
  g3or4_teae_rate_control?: unknown;
  g3or4_trae_rate_exp?: unknown;
  g3or4_trae_rate_control?: unknown;
  g3or4_ae_rate_exp?: unknown;
  g3or4_ae_rate_control?: unknown;
  g3_ae_exp?: unknown;
  g3_ae_control?: unknown;
  g4_ae_exp?: unknown;
  g4_ae_control?: unknown;
  g5ordeath_exp?: unknown;
  g5ordeath_control?: unknown;
  ae_dict?: Record<string, unknown> | null;
  [key: string]: unknown;
}

const EXP_G35_TEAE = ['g3_5_teae_exp', 'G3 5 Teae Exp', 'G3-5 TEAE Exp', 'g35_teae_exp'];
const CTRL_G35_TEAE = ['g3_5_teae_ctrl', 'g3_5_teae_control', 'G3 5 Teae Ctrl', 'G3-5 TEAE Ctrl', 'g35_teae_ctrl'];
const EXP_G35_AE = ['g3_5_ae_exp', 'G3 5 Ae Exp', 'G3-5 AE Exp', 'G3/5 AE Exp', 'g35_ae_exp', 'g345_ae_exp'];
const CTRL_G35_AE = [
  'g3_5_ae_ctrl',
  'g3_5_ae_control',
  'G3 5 Ae Ctrl',
  'G3-5 AE Ctrl',
  'G3/5 AE Ctrl',
  'g35_ae_ctrl',
  'g345_ae_ctrl',
];
const EXP_G34_TEAE = ['g3or4_teae_rate_exp', 'g3_4_teae_rate_exp'];
const CTRL_G34_TEAE = ['g3or4_teae_rate_control', 'g3or4_teae_rate_ctrl', 'g3_4_teae_rate_control'];
const EXP_G34_TRAE = ['g3or4_trae_rate_exp', 'g3_4_trae_rate_exp'];
const CTRL_G34_TRAE = ['g3or4_trae_rate_control', 'g3or4_trae_rate_ctrl', 'g3_4_trae_rate_control'];
const EXP_G34_AE = ['g3or4_ae_rate_exp', 'g3_4_ae_rate_exp'];
const CTRL_G34_AE = ['g3or4_ae_rate_control', 'g3or4_ae_rate_ctrl', 'g3_4_ae_rate_control'];
const EXP_G3_AE = ['g3_ae_exp', 'G3 Ae Exp', 'G3 AE Exp', 'g3ae_exp'];
const CTRL_G3_AE = ['g3_ae_control', 'g3_ae_ctrl', 'G3 Ae Ctrl', 'G3 AE Ctrl', 'G3 Ae Control', 'G3 AE Control'];
const EXP_G4_AE = ['g4_ae_exp', 'G4 Ae Exp', 'G4 AE Exp', 'g4ae_exp'];
const CTRL_G4_AE = ['g4_ae_control', 'g4_ae_ctrl', 'G4 Ae Ctrl', 'G4 AE Ctrl', 'G4 Ae Control', 'G4 AE Control'];
const EXP_G5 = ['g5ordeath_exp', 'g5_death_exp', 'g5ordeath_e'];
const CTRL_G5 = ['g5ordeath_control', 'g5ordeath_ctrl', 'g5_death_ctrl', 'g5_death_control'];

/** Resolve displayed G3-5 AE % for exp/ctrl from ruby_data safety columns (+ ae_dict aliases). */
export function resolveG35Ae(fields: G35AeBackendFields): ResolvedG35Ae {
  const bag = fields as Record<string, unknown>;

  const expArm = resolveG35Arm(
    pickRawField(bag, EXP_G35_TEAE),
    pickRawField(bag, EXP_G35_AE),
    pickRawField(bag, EXP_G34_TEAE),
    pickRawField(bag, EXP_G34_TRAE),
    pickRawField(bag, EXP_G34_AE),
    pickRawField(bag, EXP_G3_AE),
    pickRawField(bag, EXP_G4_AE),
    pickRawField(bag, EXP_G5),
  );
  const ctrlArm = resolveG35Arm(
    pickRawField(bag, CTRL_G35_TEAE),
    pickRawField(bag, CTRL_G35_AE),
    pickRawField(bag, CTRL_G34_TEAE),
    pickRawField(bag, CTRL_G34_TRAE),
    pickRawField(bag, CTRL_G34_AE),
    pickRawField(bag, CTRL_G3_AE),
    pickRawField(bag, CTRL_G4_AE),
    pickRawField(bag, CTRL_G5),
  );

  const source =
    expArm.source === ctrlArm.source
      ? expArm.source
      : expArm.source ?? ctrlArm.source;

  return {
    exp: expArm.value && expArm.value !== 0 ? expArm.value : null,
    ctrl: ctrlArm.value && ctrlArm.value !== 0 ? ctrlArm.value : null,
    source,
  };
}

export function formatG35AeCaption(source: G35AeSource | null | undefined): string {
  if (source === 'TEAE') return 'G3-5 TEAE';
  if (source === 'TRAE') return 'G3-5 TRAE';
  return 'G3-5 AEs';
}
